import React, { useCallback, useRef, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { dateClient } from '@utils/date-client';
import { MoreEventsPopover } from './MoreEventsPopover';
/**
 * A single day cell in the month grid.
 *
 * Renders the day number header, up to `dayMaxEvents` visible event rows,
 * and a "+N more" button when there are additional events. The "+N more"
 * button opens MoreEventsPopover which mounts all events on demand.
 *
 * Acts as a dnd-kit drop target: the cell's ISO date string is used as the
 * droppable id so MonthGrid can resolve which date the event was dropped onto.
 */
export function DayCell({ date, timezone, isCurrentMonth, isToday, events, dayMaxEvents, renderEvent, moreEventsLabel = (hiddenCount) => `+${hiddenCount} more` }) {
    const dateKey = dateClient(date).tz(timezone).format('YYYY-MM-DD');
    const { setNodeRef, isOver } = useDroppable({ id: dateKey });
    const [popoverOpen, setPopoverOpen] = useState(false);
    const moreLinkRef = useRef(null);
    const visibleEvents = events.slice(0, dayMaxEvents);
    const hiddenCount = events.length - visibleEvents.length;
    const handleMoreClick = useCallback(() => {
        setPopoverOpen(true);
    }, []);
    const handlePopoverClose = useCallback(() => {
        setPopoverOpen(false);
    }, []);
    const cellClasses = [
        'qmb-month-grid__cell',
        !isCurrentMonth && 'qmb-month-grid__cell--other-month',
        isToday && 'qmb-month-grid__cell--today',
        isOver && 'qmb-month-grid__cell--drop-over'
    ]
        .filter(Boolean)
        .join(' ');
    return (React.createElement("div", { ref: setNodeRef, className: cellClasses, "data-date": dateKey },
        React.createElement("div", { className: "qmb-month-grid__cell-header" },
            React.createElement("span", { className: "qmb-month-grid__day-number" }, dateClient(date).tz(timezone).date())),
        React.createElement("div", { className: "qmb-month-grid__cell-events" },
            visibleEvents.map((event) => (React.createElement("div", { key: event.id, className: "qmb-month-grid__event-row" }, renderEvent(event)))),
            hiddenCount > 0 && (React.createElement("button", { ref: moreLinkRef, type: "button", className: "qmb-month-grid__more-link", onClick: handleMoreClick }, moreEventsLabel(hiddenCount)))),
        popoverOpen && moreLinkRef.current && (React.createElement(MoreEventsPopover, { date: date, events: events.slice(dayMaxEvents), anchorRect: moreLinkRef.current.getBoundingClientRect(), onClose: handlePopoverClose, renderEvent: renderEvent }))));
}
